export type VideoType = "movie" | "drama" | "variety" | "short";

export interface Video {
  id: string;
  title: string;
  subtitle: string;
  type: VideoType;
  cover: string;
  backdrop: string;
  year: string;
  region: string;
  duration: string;
  rating: number;
  playCount: number;
  updateDate: string;
  description: string;
  featured: boolean;
  isNew: boolean;
  tags: string[];
}

export const categoryMeta: Record<
  VideoType,
  { label: string; slug: string; intro: string }
> = {
  movie: {
    label: "电影",
    slug: "movie",
    intro: "汇聚院线大片、口碑佳作与经典老片，覆盖科幻、犯罪、冒险、爱情、战争等多种题材，满足你对大银幕的所有想象。",
  },
  drama: {
    label: "电视剧",
    slug: "drama",
    intro: "从古装权谋到都市情感，从悬疑探案到青春校园，热播剧集每日更新，追剧不停歇。",
  },
  variety: {
    label: "综艺",
    slug: "variety",
    intro: "真人秀、音乐现场、美食探店与户外挑战，轻松解压的快乐源泉，随时打开就能乐。",
  },
  short: {
    label: "短视频",
    slug: "short",
    intro: "一分钟看世界，知识科普、城市街拍、美食小剧场与萌宠日常，碎片时间也能收获满满。",
  },
};

export const videos: Video[] = [
  {
    id: "m1",
    title: "星际边缘",
    subtitle: "穿越星海的最后坐标",
    type: "movie",
    cover:
      "https://readdy.ai/api/search-image?query=cinematic%20science%20fiction%20movie%20poster%2C%20lone%20astronaut%20in%20sleek%20spacesuit%20standing%20before%20glowing%20blue%20nebula%2C%20epic%20space%20backdrop%2C%20dramatic%20rim%20lighting%2C%20film%20grain%2C%20high%20detail%2C%20poster%20composition&width=600&height=900&seq=cover-m1&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=cinematic%20science%20fiction%20space%20scene%2C%20astronaut%20before%20giant%20glowing%20nebula%2C%20deep%20blue%20cosmic%20atmosphere%2C%20epic%20wide%20film%20still%2C%20dramatic%20lighting&width=1600&height=900&seq=back-m1&orientation=landscape",
    year: "2024",
    region: "美国",
    duration: "128分钟",
    rating: 9.2,
    playCount: 3860000,
    updateDate: "2024-09-12",
    description: "一支星际勘探队在穿越虫洞时遭遇时空错乱，队长必须在有限时间里做出抉择，带领队员回到那颗名为「星海边缘」的蓝色星球。",
    featured: true,
    isNew: true,
    tags: ["科幻", "冒险", "太空"],
  },
  {
    id: "m2",
    title: "午夜追踪",
    subtitle: "真相藏在雨夜之后",
    type: "movie",
    cover:
      "https://readdy.ai/api/search-image?query=gritty%20crime%20thriller%20movie%20poster%2C%20rainy%20neon%20city%20street%20at%20midnight%2C%20silhouette%20of%20detective%20walking%2C%20moody%20teal%20and%20orange%20lighting%2C%20cinematic%20film%20still&width=600&height=900&seq=cover-m2&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=rainy%20neon%20city%20street%20at%20night%2C%20detective%20silhouette%20walking%20away%2C%20moody%20teal%20and%20orange%20cinematic%20lighting%2C%20wide%20film%20still&width=1600&height=900&seq=back-m2&orientation=landscape",
    year: "2023",
    region: "中国大陆",
    duration: "115分钟",
    rating: 8.6,
    playCount: 2740000,
    updateDate: "2024-08-20",
    description: "一桩连环失踪案将刑警老陈拖入十年前的旧案迷局，随着雨夜真相逐渐浮现，他发现凶手远比自己想象的更近。",
    featured: true,
    isNew: false,
    tags: ["犯罪", "悬疑", "剧情"],
  },
  {
    id: "m3",
    title: "深海回响",
    subtitle: "千米之下无人知晓",
    type: "movie",
    cover:
      "https://readdy.ai/api/search-image?query=adventure%20film%20poster%2C%20deep%20ocean%20underwater%20cave%20with%20mysterious%20glowing%20light%2C%20diver%20silhouette%2C%20dark%20blue%20atmosphere%2C%20cinematic%20underwater%20photography&width=600&height=900&seq=cover-m3&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=deep%20ocean%20underwater%20cavern%20with%20mysterious%20blue%20glow%2C%20diver%20exploring%2C%20cinematic%20wide%20underwater%20scene&width=1600&height=900&seq=back-m3&orientation=landscape",
    year: "2024",
    region: "英国",
    duration: "106分钟",
    rating: 8.9,
    playCount: 1980000,
    updateDate: "2024-09-28",
    description: "深海科考队在万米海沟侦测到一段重复的信号，当他们下潜探查时，发现了一段被大海掩埋了三十年的秘密。",
    featured: true,
    isNew: true,
    tags: ["冒险", "悬疑", "深海"],
  },
  {
    id: "m4",
    title: "时光信箱",
    subtitle: "寄往过去的温柔",
    type: "movie",
    cover:
      "https://readdy.ai/api/search-image?query=romantic%20drama%20movie%20poster%2C%20warm%20sunset%20rooftop%20with%20young%20couple%2C%20soft%20golden%20light%2C%20nostalgic%20film%20tone%2C%20gentle%20bokeh%2C%20poster%20composition&width=600&height=900&seq=cover-m4&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=warm%20sunset%20city%20rooftop%20with%20young%20couple%2C%20soft%20golden%20light%2C%20nostalgic%20romantic%20cinematic%20wide%20shot&width=1600&height=900&seq=back-m4&orientation=landscape",
    year: "2022",
    region: "韩国",
    duration: "121分钟",
    rating: 8.4,
    playCount: 1560000,
    updateDate: "2024-05-16",
    description: "一间老邮局可以让信件寄往过去，女主在整理遗物时发现了母亲年轻时写给未来的信，从而拼凑出一段被遗忘的爱情。",
    featured: false,
    isNew: false,
    tags: ["爱情", "剧情", "治愈"],
  },
  {
    id: "m5",
    title: "烈日迷城",
    subtitle: "正午之前必须找到她",
    type: "movie",
    cover:
      "https://readdy.ai/api/search-image?query=suspense%20thriller%20poster%2C%20desert%20highway%20at%20blazing%20noon%20heat%2C%20lone%20car%2C%20heat%20haze%2C%20orange%20dusty%20tones%2C%20cinematic%20wide%20shot&width=600&height=900&seq=cover-m5&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=desert%20highway%20at%20blazing%20noon%2C%20lone%20car%20driving%2C%20heat%20haze%20orange%20tones%2C%20cinematic%20wide%20film%20still&width=1600&height=900&seq=back-m5&orientation=landscape",
    year: "2023",
    region: "中国大陆",
    duration: "98分钟",
    rating: 8.1,
    playCount: 1320000,
    updateDate: "2024-07-02",
    description: "沙漠公路旁的小镇接连有人失踪，卡车司机为了寻找失踪的女儿，在四十度的高温里追查到一个深埋地下的秘密。",
    featured: false,
    isNew: false,
    tags: ["悬疑", "惊悚", "公路"],
  },
  {
    id: "m6",
    title: "荒野拾荒者",
    subtitle: "在废弃之地重新开始",
    type: "movie",
    cover:
      "https://readdy.ai/api/search-image?query=drama%20film%20poster%2C%20vast%20american%20wilderness%20plain%20at%20golden%20hour%2C%20solitary%20figure%20walking%20with%20backpack%2C%20warm%20dusty%20light%2C%20cinematic%20wide%20landscape&width=600&height=900&seq=cover-m6&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=vast%20american%20wilderness%20plain%20at%20golden%20hour%2C%20solitary%20figure%20walking%2C%20warm%20dusty%20cinematic%20wide%20landscape&width=1600&height=900&seq=back-m6&orientation=landscape",
    year: "2021",
    region: "美国",
    duration: "134分钟",
    rating: 8.7,
    playCount: 1120000,
    updateDate: "2024-03-11",
    description: "经济萧条下的中年男人带着仅剩的家当穿越荒野，一路拾荒一路拾起破碎的尊严，最终找到了与过去和解的方式。",
    featured: false,
    isNew: false,
    tags: ["剧情", "公路", "人生"],
  },
  {
    id: "m7",
    title: "幻影列车",
    subtitle: "末班车驶向未知",
    type: "movie",
    cover:
      "https://readdy.ai/api/search-image?query=action%20movie%20poster%2C%20futuristic%20high%20speed%20train%20speeding%20through%20neon%20tokyo%20night%2C%20motion%20blur%20light%20trails%2C%20cool%20blue%20and%20magenta%20tones%2C%20cinematic&width=600&height=900&seq=cover-m7&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=futuristic%20high%20speed%20train%20speeding%20through%20neon%20tokyo%20at%20night%2C%20motion%20blur%20light%20trails%2C%20cinematic%20action%20wide%20shot&width=1600&height=900&seq=back-m7&orientation=landscape",
    year: "2024",
    region: "日本",
    duration: "112分钟",
    rating: 8.3,
    playCount: 2240000,
    updateDate: "2024-09-05",
    description: "一列永远到不了终点的末班列车，每位乘客都背负着不能说的过往，只有解开谜题才能下车。",
    featured: true,
    isNew: true,
    tags: ["动作", "科幻", "惊悚"],
  },
  {
    id: "m8",
    title: "白色山丘",
    subtitle: "雪落无声，归途有光",
    type: "movie",
    cover:
      "https://readdy.ai/api/search-image?query=war%20drama%20film%20poster%2C%20snowy%20mountain%20slope%20at%20dawn%2C%20lone%20soldier%20silhouette%2C%20cold%20pale%20blue%20tones%2C%20somber%20cinematic%20atmosphere&width=600&height=900&seq=cover-m8&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=snowy%20mountain%20battlefield%20at%20dawn%2C%20lone%20soldier%20silhouette%2C%20cold%20pale%20blue%20cinematic%20wide%20shot&width=1600&height=900&seq=back-m8&orientation=landscape",
    year: "2022",
    region: "法国",
    duration: "145分钟",
    rating: 9.0,
    playCount: 1680000,
    updateDate: "2024-04-18",
    description: "战争持续的第三个冬天，一名年轻的通讯兵被困在白色山丘的哨所，他必须独自守住阵地与心中的信念。",
    featured: false,
    isNew: false,
    tags: ["战争", "剧情", "历史"],
  },
  {
    id: "d1",
    title: "长夜将明",
    subtitle: "一子落，满盘皆惊",
    type: "drama",
    cover:
      "https://readdy.ai/api/search-image?query=chinese%20historical%20palace%20drama%20poster%2C%20ornate%20ancient%20palace%20hall%20at%20night%20with%20lanterns%2C%20figure%20in%20flowing%20hanfu%2C%20elegant%20cinematic%20tone%2C%20rich%20warm%20light&width=600&height=900&seq=cover-d1&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=ornate%20ancient%20chinese%20palace%20hall%20at%20night%20with%20red%20lanterns%2C%20figure%20in%20flowing%20hanfu%2C%20cinematic%20wide%20period%20drama%20shot&width=1600&height=900&seq=back-d1&orientation=landscape",
    year: "2024",
    region: "中国大陆",
    duration: "45分钟/集",
    rating: 9.1,
    playCount: 5240000,
    updateDate: "2024-09-30",
    description: "一场宫宴上的暗杀，让原本远离朝堂的落魄公子被卷入权力漩涡，他要在漫漫长夜里为家族洗清冤屈。",
    featured: true,
    isNew: true,
    tags: ["古装", "权谋", "剧情"],
  },
  {
    id: "d2",
    title: "都市浮生",
    subtitle: "在这座城市里，我们都在奔跑",
    type: "drama",
    cover:
      "https://readdy.ai/api/search-image?query=modern%20urban%20romance%20tv%20drama%20poster%2C%20stylish%20city%20skyline%20at%20dusk%2C%20young%20professionals%20on%20rooftop%2C%20warm%20city%20lights%2C%20cinematic%20contemporary%20tone&width=600&height=900&seq=cover-d2&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=modern%20city%20skyline%20at%20dusk%2C%20young%20professionals%20on%20rooftop%20talking%2C%20warm%20city%20lights%2C%20cinematic%20contemporary%20wide%20shot&width=1600&height=900&seq=back-d2&orientation=landscape",
    year: "2023",
    region: "中国大陆",
    duration: "42分钟/集",
    rating: 8.5,
    playCount: 3620000,
    updateDate: "2024-08-01",
    description: "三位性格迥异的年轻人在同一栋写字楼里相遇，他们在职场与情感的双重压力下，寻找属于自己的生活答案。",
    featured: true,
    isNew: false,
    tags: ["都市", "爱情", "职场"],
  },
  {
    id: "d3",
    title: "迷雾档案",
    subtitle: "每一个案子都有它的影子",
    type: "drama",
    cover:
      "https://readdy.ai/api/search-image?query=crime%20mystery%20tv%20series%20poster%2C%20dim%20cluttered%20investigation%20office%20with%20photographs%20on%20wall%2C%20single%20desk%20lamp%20glow%2C%20moody%20cinematic&width=600&height=900&seq=cover-d3&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=dim%20investigation%20office%20with%20evidence%20photographs%20on%20wall%2C%20single%20desk%20lamp%20glow%2C%20moody%20cinematic%20wide%20shot&width=1600&height=900&seq=back-d3&orientation=landscape",
    year: "2024",
    region: "中国大陆",
    duration: "48分钟/集",
    rating: 8.8,
    playCount: 4180000,
    updateDate: "2024-09-25",
    description: "重案组新来了一位擅长心理侧写的顾问，他与老刑警搭档，用一份份尘封档案拼出迷雾背后的真相。",
    featured: false,
    isNew: true,
    tags: ["悬疑", "犯罪", "刑侦"],
  },
  {
    id: "d4",
    title: "山海秘境",
    subtitle: "万物有灵，山海有约",
    type: "drama",
    cover:
      "https://readdy.ai/api/search-image?query=fantasy%20tv%20drama%20poster%2C%20misty%20ancient%20mountain%20range%20with%20floating%20islands%2C%20dragon%20silhouette%2C%20ethereal%20teal%20and%20gold%20tones%2C%20epic%20scale&width=600&height=900&seq=cover-d4&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=ethereal%20misty%20ancient%20mountains%20with%20floating%20islands%2C%20dragon%20silhouette%20in%20clouds%2C%20teal%20and%20gold%20epic%20fantasy%20wide%20shot&width=1600&height=900&seq=back-d4&orientation=landscape",
    year: "2024",
    region: "中国大陆",
    duration: "45分钟/集",
    rating: 8.9,
    playCount: 4760000,
    updateDate: "2024-09-18",
    description: "少年在深山中捡到一枚会发光的鳞片，从此踏入山海之间的秘境，卷入人类与灵兽延续千年的约定。",
    featured: true,
    isNew: true,
    tags: ["奇幻", "古装", "冒险"],
  },
  {
    id: "d5",
    title: "青春纪事",
    subtitle: "那个夏天，我们都还年轻",
    type: "drama",
    cover:
      "https://readdy.ai/api/search-image?query=youth%20campus%20drama%20poster%2C%20bright%20high%20school%20classroom%20with%20afternoon%20sunlight%2C%20students%20by%20window%2C%20fresh%20warm%20nostalgic%20tone%2C%20soft%20focus&width=600&height=900&seq=cover-d5&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=bright%20high%20school%20campus%20with%20afternoon%20sunlight%2C%20students%20by%20window%2C%20warm%20nostalgic%20cinematic%20wide%20shot&width=1600&height=900&seq=back-d5&orientation=landscape",
    year: "2022",
    region: "中国大陆",
    duration: "40分钟/集",
    rating: 8.2,
    playCount: 2140000,
    updateDate: "2024-06-10",
    description: "高三（7）班的六个同学在最后一个夏天里经历了友情、懵懂与分别，多年后他们再次回到那间洒满阳光的教室。",
    featured: false,
    isNew: false,
    tags: ["青春", "校园", "治愈"],
  },
  {
    id: "d6",
    title: "归途列车",
    subtitle: "无论多远，家都在等你",
    type: "drama",
    cover:
      "https://readdy.ai/api/search-image?query=family%20drama%20tv%20poster%2C%20old%20countryside%20railway%20station%20at%20golden%20sunset%2C%20family%20figure%20with%20luggage%2C%20warm%20nostalgic%20cinematic%20light&width=600&height=900&seq=cover-d6&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=old%20countryside%20railway%20station%20at%20golden%20sunset%2C%20traveler%20with%20luggage%2C%20warm%20nostalgic%20cinematic%20wide%20shot&width=1600&height=900&seq=back-d6&orientation=landscape",
    year: "2023",
    region: "中国大陆",
    duration: "44分钟/集",
    rating: 8.6,
    playCount: 2860000,
    updateDate: "2024-07-22",
    description: "一对多年未和的父女因为一趟回乡的绿皮火车被迫同行，旅途中的争吵与沉默让彼此重新读懂了对方。",
    featured: false,
    isNew: false,
    tags: ["家庭", "剧情", "治愈"],
  },
  {
    id: "d7",
    title: "宫廷迷案",
    subtitle: "一盏灯，照见深宫人心",
    type: "drama",
    cover:
      "https://readdy.ai/api/search-image?query=historical%20mystery%20drama%20poster%2C%20candle%20lit%20ancient%20chinese%20study%20with%20scrolls%20and%20lanterns%2C%20mysterious%20shadowy%20figure%2C%20moody%20warm%20light&width=600&height=900&seq=cover-d7&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=candle%20lit%20ancient%20chinese%20study%20with%20scrolls%20and%20lanterns%2C%20mysterious%20shadowy%20figure%2C%20moody%20warm%20cinematic%20period%20shot&width=1600&height=900&seq=back-d7&orientation=landscape",
    year: "2023",
    region: "中国大陆",
    duration: "46分钟/集",
    rating: 8.4,
    playCount: 1980000,
    updateDate: "2024-05-30",
    description: "宫中一夜之间三位要员离奇死亡，负责查案的女官必须在真相与权势之间走钢丝，揭开一场精心策划的阴谋。",
    featured: false,
    isNew: false,
    tags: ["古装", "悬疑", "探案"],
  },
  {
    id: "d8",
    title: "商海风云",
    subtitle: "棋局之上，谁主沉浮",
    type: "drama",
    cover:
      "https://readdy.ai/api/search-image?query=business%20drama%20tv%20poster%2C%20modern%20glass%20skyscrapers%20at%20blue%20hour%2C%20confident%20executive%20silhouette%2C%20cool%20cinematic%20corporate%20tone&width=600&height=900&seq=cover-d8&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=modern%20glass%20skyscrapers%20at%20blue%20hour%2C%20confident%20executive%20silhouette%20before%20window%2C%20cool%20cinematic%20corporate%20wide%20shot&width=1600&height=900&seq=back-d8&orientation=landscape",
    year: "2024",
    region: "中国大陆",
    duration: "45分钟/集",
    rating: 8.3,
    playCount: 2420000,
    updateDate: "2024-09-08",
    description: "家族企业接班人之争一触即发，年轻的总裁助理在资本博弈中步步为营，最终成长为掌舵巨轮的操盘手。",
    featured: false,
    isNew: true,
    tags: ["商战", "都市", "剧情"],
  },
  {
    id: "v1",
    title: "欢乐向前冲",
    subtitle: "每周五，快乐出发",
    type: "variety",
    cover:
      "https://readdy.ai/api/search-image?query=energetic%20variety%20show%20poster%2C%20colorful%20studio%20stage%20with%20bright%20lights%20and%20confetti%2C%20playful%20hosts%20celebrating%2C%20vibrant%20cheerful%20tone&width=600&height=900&seq=cover-v1&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=colorful%20variety%20show%20studio%20stage%20with%20bright%20lights%20and%20confetti%2C%20hosts%20celebrating%2C%20vibrant%20cheerful%20wide%20shot&width=1600&height=900&seq=back-v1&orientation=landscape",
    year: "2024",
    region: "中国大陆",
    duration: "90分钟/期",
    rating: 8.7,
    playCount: 4520000,
    updateDate: "2024-10-01",
    description: "明星嘉宾与素人选手组队闯关，用最热血的游戏挑战和爆笑互动陪你度过每个周末夜晚。",
    featured: true,
    isNew: true,
    tags: ["真人秀", "游戏", "搞笑"],
  },
  {
    id: "v2",
    title: "星光大道之夜",
    subtitle: "让世界听见你的声音",
    type: "variety",
    cover:
      "https://readdy.ai/api/search-image?query=glamorous%20music%20talent%20show%20poster%2C%20shining%20stage%20spotlights%20and%20audience%20silhouettes%2C%20golden%20beams%2C%20dramatic%20entertainment%20lighting&width=600&height=900&seq=cover-v2&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=glamorous%20music%20talent%20show%20stage%20with%20golden%20spotlights%20and%20audience%20silhouettes%2C%20dramatic%20wide%20entertainment%20shot&width=1600&height=900&seq=back-v2&orientation=landscape",
    year: "2024",
    region: "中国大陆",
    duration: "100分钟/期",
    rating: 8.5,
    playCount: 3280000,
    updateDate: "2024-09-27",
    description: "来自全国的新声代歌手同台竞技，用歌声讲述梦想，每一位站上舞台的人都值得被看见。",
    featured: false,
    isNew: true,
    tags: ["音乐", "选秀", "舞台"],
  },
  {
    id: "v3",
    title: "美食探店记",
    subtitle: "跟着镜头吃遍一座城",
    type: "variety",
    cover:
      "https://readdy.ai/api/search-image?query=food%20variety%20show%20poster%2C%20cozy%20restaurant%20table%20filled%20with%20steaming%20delicious%20dishes%2C%20warm%20ambient%20lighting%2C%20appetizing%20top%20down%20angle&width=600&height=900&seq=cover-v3&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=cozy%20restaurant%20table%20filled%20with%20steaming%20delicious%20dishes%2C%20warm%20ambient%20lighting%2C%20appetizing%20wide%20food%20shot&width=1600&height=900&seq=back-v3&orientation=landscape",
    year: "2024",
    region: "中国大陆",
    duration: "45分钟/期",
    rating: 8.9,
    playCount: 2960000,
    updateDate: "2024-09-20",
    description: "三位吃货主持人深入街头巷尾，寻找隐藏在城市角落里的宝藏小店，用一餐饭的时间认识一座城。",
    featured: true,
    isNew: false,
    tags: ["美食", "探店", "生活"],
  },
  {
    id: "v4",
    title: "极限挑战季",
    subtitle: "突破极限，挑战自我",
    type: "variety",
    cover:
      "https://readdy.ai/api/search-image?query=outdoor%20adventure%20variety%20show%20poster%2C%20contestants%20on%20rugged%20cliffside%20obstacle%20course%2C%20bright%20daylight%2C%20dynamic%20energetic%20composition&width=600&height=900&seq=cover-v4&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=contestants%20on%20rugged%20cliffside%20obstacle%20course%20in%20bright%20daylight%2C%20dynamic%20energetic%20wide%20adventure%20shot&width=1600&height=900&seq=back-v4&orientation=landscape",
    year: "2023",
    region: "中国大陆",
    duration: "80分钟/期",
    rating: 8.4,
    playCount: 2380000,
    updateDate: "2024-08-14",
    description: "八位嘉宾被投放到荒野之中，只有通过层层极限挑战才能赢得最终的荣誉与奖励。",
    featured: false,
    isNew: false,
    tags: ["挑战", "户外", "竞技"],
  },
  {
    id: "v5",
    title: "音乐现场show",
    subtitle: "一次酣畅淋漓的live",
    type: "variety",
    cover:
      "https://readdy.ai/api/search-image?query=live%20music%20concert%20poster%2C%20band%20performing%20on%20stage%20with%20colorful%20stage%20lights%20and%20haze%2C%20energetic%20crowd%20silhouettes%2C%20vibrant%20concert%20lighting&width=600&height=900&seq=cover-v5&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=band%20performing%20live%20on%20stage%20with%20colorful%20lights%20and%20haze%2C%20crowd%20silhouettes%2C%20vibrant%20concert%20wide%20shot&width=1600&height=900&seq=back-v5&orientation=landscape",
    year: "2024",
    region: "中国大陆",
    duration: "75分钟/期",
    rating: 8.6,
    playCount: 1840000,
    updateDate: "2024-09-14",
    description: "每期邀请一支乐队带来完整现场演出，近距离记录音乐人最真实、最热烈的舞台瞬间。",
    featured: false,
    isNew: false,
    tags: ["音乐", "现场", "live"],
  },
  {
    id: "v6",
    title: "荒野求生记",
    subtitle: "在自然里学会敬畏",
    type: "variety",
    cover:
      "https://readdy.ai/api/search-image?query=outdoor%20survival%20reality%20show%20poster%2C%20wild%20forest%20campfire%20at%20dusk%20with%20tents%2C%20golden%20campfire%20glow%2C%20adventurous%20atmospheric%20tone&width=600&height=900&seq=cover-v6&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=wild%20forest%20campfire%20at%20dusk%20with%20tents%2C%20golden%20campfire%20glow%2C%20adventurous%20atmospheric%20wide%20shot&width=1600&height=900&seq=back-v6&orientation=landscape",
    year: "2023",
    region: "中国大陆",
    duration: "60分钟/期",
    rating: 8.2,
    playCount: 1420000,
    updateDate: "2024-06-28",
    description: "跟随探险家深入原始森林，在没有任何现代工具的情况下学习生火、觅食与搭建庇护所。",
    featured: false,
    isNew: false,
    tags: ["户外", "求生", "纪实"],
  },
  {
    id: "s1",
    title: "一分钟看懂宇宙",
    subtitle: "把浩瀚装进六十秒",
    type: "short",
    cover:
      "https://readdy.ai/api/search-image?query=educational%20short%20video%20cover%2C%20colorful%20animated%20solar%20system%20and%20galaxies%20illustration%2C%20dark%20space%20background%2C%20vibrant%20infographic%20style&width=600&height=900&seq=cover-s1&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=colorful%20animated%20solar%20system%20and%20galaxies%20illustration%2C%20dark%20space%20background%2C%20vibrant%20infographic%20wide%20shot&width=1600&height=900&seq=back-s1&orientation=landscape",
    year: "2024",
    region: "中国大陆",
    duration: "1分20秒",
    rating: 8.8,
    playCount: 6120000,
    updateDate: "2024-10-02",
    description: "用六十秒的动画带你快速了解一个宇宙知识点，从黑洞到星系，把复杂的科学讲得简单有趣。",
    featured: true,
    isNew: true,
    tags: ["科普", "动画", "知识"],
  },
  {
    id: "s2",
    title: "城市街拍日记",
    subtitle: "记录街头的每一帧",
    type: "short",
    cover:
      "https://readdy.ai/api/search-image?query=street%20style%20vlog%20cover%2C%20fashionable%20young%20people%20walking%20on%20stylish%20city%20street%2C%20golden%20hour%2C%20candid%20lifestyle%20photography&width=600&height=900&seq=cover-s2&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=fashionable%20young%20people%20walking%20on%20stylish%20city%20street%20at%20golden%20hour%2C%20candid%20lifestyle%20wide%20shot&width=1600&height=900&seq=back-s2&orientation=landscape",
    year: "2024",
    region: "中国大陆",
    duration: "2分10秒",
    rating: 8.3,
    playCount: 3480000,
    updateDate: "2024-09-29",
    description: "每天用镜头记录城市街头的穿搭、光影与故事，捕捉那些稍纵即逝的生活瞬间。",
    featured: false,
    isNew: true,
    tags: ["街拍", "穿搭", "生活"],
  },
  {
    id: "s3",
    title: "美食小剧场",
    subtitle: "三十秒治愈你的胃",
    type: "short",
    cover:
      "https://readdy.ai/api/search-image?query=short%20food%20video%20cover%2C%20close%20up%20of%20sizzling%20ramen%20bowl%20with%20fresh%20toppings%2C%20vibrant%20colors%2C%20appetizing%20food%20photography&width=600&height=900&seq=cover-s3&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=close%20up%20of%20sizzling%20ramen%20bowl%20with%20fresh%20toppings%2C%20vibrant%20colors%2C%20appetizing%20wide%20food%20shot&width=1600&height=900&seq=back-s3&orientation=landscape",
    year: "2024",
    region: "中国大陆",
    duration: "45秒",
    rating: 8.6,
    playCount: 4260000,
    updateDate: "2024-09-26",
    description: "把做菜的每一个细节拍成一出小短剧，短短几十秒，就能让你胃口大开。",
    featured: false,
    isNew: false,
    tags: ["美食", "教程", "治愈"],
  },
  {
    id: "s4",
    title: "搞笑瞬间合集",
    subtitle: "一秒一个笑点",
    type: "short",
    cover:
      "https://readdy.ai/api/search-image?query=funny%20moments%20short%20video%20cover%2C%20people%20laughing%20joyfully%20in%20bright%20playful%20setting%2C%20colorful%20pop%20art%20style%2C%20cheerful%20energetic&width=600&height=900&seq=cover-s4&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=people%20laughing%20joyfully%20in%20bright%20playful%20setting%2C%20colorful%20pop%20art%20style%2C%20cheerful%20wide%20shot&width=1600&height=900&seq=back-s4&orientation=landscape",
    year: "2024",
    region: "中国大陆",
    duration: "1分05秒",
    rating: 8.1,
    playCount: 5280000,
    updateDate: "2024-09-22",
    description: "精选网络上最受欢迎的搞笑片段，随时随地打开就能笑出声，治愈你的坏心情。",
    featured: false,
    isNew: false,
    tags: ["搞笑", "娱乐", "合集"],
  },
  {
    id: "s5",
    title: "旅行vlog精选",
    subtitle: "把远方装进镜头",
    type: "short",
    cover:
      "https://readdy.ai/api/search-image?query=travel%20vlog%20cover%2C%20aerial%20view%20of%20turquoise%20tropical%20coastline%20with%20small%20boat%2C%20bright%20sunny%20vibrant%20tones%2C%20wanderlust%20photography&width=600&height=900&seq=cover-s5&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=aerial%20view%20of%20turquoise%20tropical%20coastline%20with%20small%20boat%2C%20bright%20sunny%20vibrant%20wide%20travel%20shot&width=1600&height=900&seq=back-s5&orientation=landscape",
    year: "2024",
    region: "中国大陆",
    duration: "2分40秒",
    rating: 8.7,
    playCount: 2640000,
    updateDate: "2024-09-16",
    description: "用航拍与第一视角带你云游世界，从海岛到雪山，每一帧都是值得收藏的风景。",
    featured: false,
    isNew: false,
    tags: ["旅行", "vlog", "风景"],
  },
  {
    id: "s6",
    title: "萌宠日常记录",
    subtitle: "被可爱狠狠治愈",
    type: "short",
    cover:
      "https://readdy.ai/api/search-image?query=cute%20pet%20short%20video%20cover%2C%20fluffy%20kitten%20and%20puppy%20playing%20in%20bright%20cozy%20room%2C%20soft%20natural%20light%2C%20adorable%20heartwarming%20tone&width=600&height=900&seq=cover-s6&orientation=portrait",
    backdrop:
      "https://readdy.ai/api/search-image?query=fluffy%20kitten%20and%20puppy%20playing%20in%20bright%20cozy%20room%2C%20soft%20natural%20light%2C%20adorable%20heartwarming%20wide%20shot&width=1600&height=900&seq=back-s6&orientation=landscape",
    year: "2024",
    region: "中国大陆",
    duration: "55秒",
    rating: 8.9,
    playCount: 3860000,
    updateDate: "2024-09-30",
    description: "记录家里猫猫狗狗的日常憨憨瞬间，用最纯粹的可爱治愈每一个疲惫的你。",
    featured: true,
    isNew: true,
    tags: ["萌宠", "搞笑", "治愈"],
  },
];

export const regions: string[] = [
  "中国大陆",
  "美国",
  "英国",
  "韩国",
  "日本",
  "法国",
];

export const years: string[] = ["2024", "2023", "2022", "2021"];